import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { parseISO, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import apiService from '../services/api';
import { AlertCircle, CheckCircle, Info, AlertTriangle, XCircle, Trash2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { Alert, AlertsResponse } from '../types/api';

const AlertsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [acknowledgingIds, setAcknowledgingIds] = useState<Set<number>>(new Set());
  const [clearingAll, setClearingAll] = useState(false);

  // Helper function to format UTC timestamp to local timezone
  const formatUtcToLocal = (utcTimestamp: string): string => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Ensure timestamp is treated as UTC - append 'Z' if no timezone info
    const utcString = utcTimestamp.endsWith('Z') || utcTimestamp.includes('+') || (utcTimestamp.match(/[-+]\d{2}:\d{2}$/))
      ? utcTimestamp 
      : utcTimestamp + 'Z';
    return format(utcToZonedTime(parseISO(utcString), userTimezone), 'MMM dd, yyyy HH:mm:ss');
  };

  const { data: alertsResponse, isLoading } = useQuery<AlertsResponse>({
    queryKey: ['alerts-count'],
    queryFn: (): Promise<AlertsResponse> => apiService.getAlerts(false),
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 5000,
  });

  const alerts: Alert[] = alertsResponse?.alerts ?? [];
  const unacknowledgedAlerts = alerts.filter(a => !a.is_acknowledged);
  const acknowledgedAlerts = alerts.filter(a => a.is_acknowledged);

  const displayedAlerts = showAcknowledged ? alerts : unacknowledgedAlerts;

  const getAlertIcon = (alertType: string) => {
    switch (alertType.toLowerCase()) {
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getAlertColor = (alertType: string) => {
    switch (alertType.toLowerCase()) {
      case 'error':
        return 'border-red-200 bg-red-50';
      case 'warning':
        return 'border-orange-200 bg-orange-50';
      case 'info':
        return 'border-blue-200 bg-blue-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const handleAcknowledge = async (alertId: number) => {
    setAcknowledgingIds(prev => new Set(prev).add(alertId));
    try {
      await apiService.acknowledgeAlert(alertId);
      toast.success('Alert acknowledged');
      queryClient.invalidateQueries({ queryKey: ['alerts-count'] });
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to acknowledge alert';
      toast.error(errorMsg);
    } finally {
      setAcknowledgingIds(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    }
  };

  const handleClearAll = async () => {
    if (unacknowledgedAlerts.length === 0) return;
    
    setClearingAll(true);
    try {
      // Acknowledge all unacknowledged alerts in parallel
      await Promise.all(
        unacknowledgedAlerts.map(alert => apiService.acknowledgeAlert(alert.id))
      );
      toast.success(`Cleared ${unacknowledgedAlerts.length} alert(s)`);
      queryClient.invalidateQueries({ queryKey: ['alerts-count'] });
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to clear alerts';
      toast.error(errorMsg);
    } finally {
      setClearingAll(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
          <p className="mt-1 text-sm text-gray-500">
            {unacknowledgedAlerts.length} unacknowledged, {acknowledgedAlerts.length} acknowledged
          </p>
        </div>
        {unacknowledgedAlerts.length > 0 && (
          <button
            onClick={handleClearAll}
            disabled={clearingAll}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {clearingAll ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                <span>Clearing...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                <span>Clear All</span>
              </>
            )}
          </button>
        )}
      </div>

      {acknowledgedAlerts.length > 0 && (
        <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="showAcknowledged"
            checked={showAcknowledged}
            onChange={(e) => setShowAcknowledged(e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label htmlFor="showAcknowledged" className="text-sm text-gray-700 cursor-pointer">
            Show acknowledged alerts
          </label>
        </div>
      )}

      {displayedAlerts.length === 0 ? (
        <div className="card text-center py-12">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {showAcknowledged ? 'No alerts' : 'No unacknowledged alerts'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayedAlerts
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .map((alert) => (
              <div
                key={alert.id}
                className={`card border-l-4 ${getAlertColor(alert.alert_type)} ${
                  alert.is_acknowledged ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {getAlertIcon(alert.alert_type)}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-semibold text-gray-900">{alert.title}</h3>
                        {alert.is_acknowledged && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Acknowledged
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-600">{alert.message}</p>
                      <p className="mt-2 text-xs text-gray-500">
                        {formatUtcToLocal(alert.created_at)}
                        {alert.acknowledged_at && (
                          <span className="ml-2">
                            • Acknowledged {formatUtcToLocal(alert.acknowledged_at)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {!alert.is_acknowledged && (
                    <button
                      onClick={() => handleAcknowledge(alert.id)}
                      disabled={acknowledgingIds.has(alert.id)}
                      className="ml-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {acknowledgingIds.has(alert.id) ? (
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        'Acknowledge'
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default AlertsPage;
